import { useEffect, useState } from "react";
import { apiClient } from "../api/apiClient.js";

/**
 * Fetches GET /api/backoffice/hierarchy/tree?perspective=X once per
 * perspective. The tree already contains the full Super Agent -> PLC ->
 * Agent structure the current actor is scoped to see, so every list level
 * (super agents, PLCs under one, agents under one) is derived client-side
 * from this single fetch rather than issuing a new request per depth.
 */
export function useHierarchyTree(perspective) {
  const [state, setState] = useState({ status: "loading", data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading", data: null, error: null });

    apiClient
      .get(`/backoffice/hierarchy/tree?perspective=${encodeURIComponent(perspective)}`)
      .then((data) => {
        if (!cancelled) {
          setState({ status: "ready", data, error: null });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setState({ status: "error", data: null, error });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [perspective]);

  return state;
}

/** Finds the Super Agent node for a given id in an already-loaded tree. */
export function findSuperAgent(tree, superAgentId) {
  return tree?.superAgents?.find((sa) => sa.organizationId === superAgentId) ?? null;
}

/** Finds the PLC node (and its parent Super Agent) for a given id. */
export function findPlc(tree, superAgentId, plcId) {
  const superAgent = findSuperAgent(tree, superAgentId);
  const plc = superAgent?.plcs?.find((p) => p.organizationId === plcId) ?? null;
  return { superAgent, plc };
}

/** Finds the Agent node (and its parent PLC/Super Agent) for a given id. */
export function findAgent(tree, superAgentId, plcId, agentId) {
  const { superAgent, plc } = findPlc(tree, superAgentId, plcId);
  const agent = plc?.agents?.find((a) => a.agentId === agentId) ?? null;
  return { superAgent, plc, agent };
}
